import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    useTable,
    usePagination,
    useSortBy,
    useFilters,
    useExpanded,
    useResizeColumns,
    useFlexLayout,
    useColumnOrder,
    useBlockLayout,
} from 'react-table'
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    TableContainer,
    TableSortLabel,
} from '@mui/material'
import Cell from '../../../../elem/widgets/grid/Cell'
import stringFields from '../../../../../utils/constants/widgets/stringFields'
import getHiddenColumns from '../../../../../utils/widgets/getHiddenColumns'
import Header from './Header'
import styled from 'styled-components'
import ColumnSelectionModal from './ColumnSelectionModal'
import DefaultSortTypeFunction from '../../../../../utils/DefaultSortTypeFunction'
import SearchField from './SearchField'
import dateFields from '../../../../../utils/constants/widgets/dateFields'
import dayjs from 'dayjs'
import dateTimeFields from '../../../../../utils/constants/widgets/dateTimeFields'
import { formatDate } from '../../../../../utils/valueFormatting'
import {
    operatorFilterFunction,
    getColumnFieldType,
} from '../../../../../utils/filters/filterOperators'

const StyledTHeader = styled(TableHead)`
    position: sticky;
    top: 0;
`

const createDateField = (col, isTime = false) => {
    const dateFormat = isTime ? 'MM/DD/YYYY HH:mm:ss' : 'MM/DD/YYYY'
    const INVALID_DATE = '01/01/1002'

    const formatCellValue = (value) => {
        if (!value) return ''

        const date = dayjs(value)
        if (!date.isValid()) return ''

        const formatted = date.format(dateFormat)
        return formatted.includes(INVALID_DATE) ? '' : formatted
    }

    return {
        Header: col,
        id: col,
        accessor: (row) => formatDate(row[col], isTime),
        Cell: ({ cell }) => {
            return (
                <Cell
                    value={formatCellValue(cell.value)}
                    col={col}
                    type="string"
                    extraCSS={`reportCell`}
                />
            )
        },
        type: 'DateTime',
        filter: operatorFilterFunction,
        fieldType: 'date',
        sortType: (a, b) =>
            dayjs(b.values[col]).toDate() - dayjs(a.values[col]).toDate(),
    }
}

const parseColumns = (data, valueLabel, sharedKey, widget) => {
    const exampleRow = data[0] // get the first row of the data to extract cols from
    if (!exampleRow) {
        return []
    }
    const hiddenColumns = getHiddenColumns(widget)

    const columns = Object.keys(exampleRow)
        .filter((col) => col !== 'Value') // remove the value column, we'll add next w/ proper label
        .filter((col) => !hiddenColumns.includes(col))
        .filter((col) => (sharedKey ? col !== sharedKey : true)) // if there is a shared key, we don't want it to display in the grid
        .map((col) => {
            const isDateField =
                !stringFields.includes(col) &&
                isNaN(exampleRow[col]) &&
                ([...dateFields, ...dateTimeFields].includes(col) ||
                    dayjs(exampleRow[col]).isValid())

            if (isDateField) {
                const isTime = dateTimeFields.includes(col)
                return createDateField(col, isTime)
            }

            const fieldType = getColumnFieldType(col, data)

            return {
                Header: col,
                accessor: col,
                Cell: ({ cell }) => {
                    return (
                        <Cell
                            value={cell.value}
                            col={col}
                            type={
                                stringFields.includes(col) &&
                                !(
                                    cell.value &&
                                    cell.value.toString().startsWith('<a')
                                )
                                    ? 'string'
                                    : null
                            }
                            extraCSS={`reportCell`}
                        />
                    )
                },
                type: 'String',
                filter: operatorFilterFunction,
                fieldType,
                sortType: DefaultSortTypeFunction(exampleRow, col, data),
            }
        }) // map column name => {Header: <column_name>, accessor: <column_name>} format

    return columns
}

const ReportTable = ({
    data,
    loading,
    updateQueryParameters,
    queryParameters,
    hideHeader = false,
    hideColumnSettings = false,
}) => {
    const [gridConfigExpanded, setGridConfigExpanded] = useState(false)

    const columns = useMemo(() => {
        const widget = {
            ValueLabel: 'Value',
            SharedKey: null,
        }
        const c = parseColumns(
            data,
            widget.ValueLabel,
            widget.SharedKey,
            widget
        )
        return c
    }, [data])

    const tableData = useMemo(() => data, [data])
    const defaultColumn = useMemo(
        () => ({
            // minWidth: 200, // minWidth is only used as a limit for resizing
            width: 200, // width is used for both the flex-basis and flex-grow
            maxWidth: 400, // maxWidth is only used as a limit for resizing
        }),
        []
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        rows,
        allColumns,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        // selectedFlatRows,
        setAllFilters,
        setColumnOrder,
        state: {
            pageIndex,
            pageSize,
            filters,
            sortBy,
            columnOrder,
            hiddenColumns,
        },
    } = useTable(
        {
            columns,
            data: tableData,
            defaultColumn,
            autoResetPage: false,
            autoResetSelectedRows: false,
            // manualPagination: true,
            // pageCount: 25, //the initial page count
            manualSortBy: false,
            autoResetSortBy: false,
            disableSortRemove: true,
            initialState: {
                pageIndex: 0,
                pageSize: 25,
                // filters: gridQuery,
                // gridLayout: width,
            },
            disableMultiSort: true,
            autoResetFilters: false,
        },
        useFilters,
        useSortBy,
        useBlockLayout,
        useExpanded,
        usePagination,
        useResizeColumns,
        useColumnOrder
    )

    const count = useMemo(() => rows && rows.length, [rows])
    const pages = useMemo(
        () => (count && !isNaN(pageSize) ? count / pageSize + 1 : 0),
        [count, pageSize]
    )

    // when the page size changes, go to the 0th page
    useEffect(() => {
        gotoPage(0)
    }, [gotoPage, pageSize])

    useEffect(
        () =>
            updateQueryParameters({
                rows,
                filters,
                currentReportData: rows.map((x) => x.original),
                sortBy,
                columnOrder,
                hiddenColumns,
            }),
        [
            rows,
            filters,
            sortBy,
            columnOrder,
            hiddenColumns,
            updateQueryParameters,
        ]
    )

    const paginationProps = useMemo(
        () => ({
            pageSize,
            pageIndex,
            page,
            nextPage,
            gotoPage,
            previousPage,
            setPageSize,
            pages,
            count,
        }),
        [
            pageSize,
            page,
            pageIndex,
            setPageSize,
            gotoPage,
            nextPage,
            previousPage,
            pages,
            count,
        ]
    )

    const clearFilters = useCallback(() => setAllFilters([]), [setAllFilters])

    return (
        <>
            {!hideHeader && (
                <Header
                    {...paginationProps}
                    loading={loading}
                    clearFunction={clearFilters}
                    setGridConfigExpanded={setGridConfigExpanded}
                    gridConfigExpanded={gridConfigExpanded}
                />
            )}
            {!hideColumnSettings && (
                <ColumnSelectionModal
                    columns={allColumns}
                    setColumnOrder={setColumnOrder}
                    gridConfigExpanded={gridConfigExpanded}
                />
            )}
            <TableContainer>
                <div className="gridWrapper reportGridWrapper">
                    <Table {...getTableProps()} size={'small'} stickyHeader>
                        <StyledTHeader>
                            {headerGroups.map((headerGroup, index) => (
                                <TableRow
                                    {...headerGroup.getHeaderGroupProps()}
                                    key={index}
                                    className="tr"
                                >
                                    {headerGroup.headers.map((column, i) => (
                                        <TableCell
                                            {...column.getHeaderProps()}
                                            sx={{ display: 'flex' }}
                                            key={i}
                                        >
                                            <div className="tableHeaderWrapper">
                                                <div>
                                                    <span
                                                        {...column.getSortByToggleProps(
                                                            {
                                                                className:
                                                                    'gridColumnHeader sticky th',
                                                            }
                                                        )}
                                                    >
                                                        {column.render(
                                                            'Header'
                                                        )}
                                                        <TableSortLabel
                                                            active={
                                                                column.isSorted
                                                            }
                                                            // react-table has a unsorted state which is not treated here
                                                            direction={
                                                                column.isSortedDesc
                                                                    ? 'desc'
                                                                    : 'asc'
                                                            }
                                                        />
                                                    </span>
                                                    {column.canResize && (
                                                        <div
                                                            {...column.getResizerProps()}
                                                            className={`resizer`}
                                                        />
                                                    )}
                                                </div>
                                                {/* <div className="tableFilterWrapper">
                                                            {column.canFilter
                                                                ? column.render('Filter')
                                                                : null}
                                                        </div> */}
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {headerGroups.map((headerGroup) => (
                                <TableRow
                                    {...headerGroup.getHeaderGroupProps()}
                                    sx={{ display: 'flex' }}
                                >
                                    {headerGroup.headers.map((column, idx) => {
                                        return column.canFilter ? (
                                            <SearchField key={column.id} column={column} />
                                        ) : null
                                    })}
                                </TableRow>
                            ))}
                        </StyledTHeader>
                        <TableBody>
                            {page.map((row) => {
                                prepareRow(row)
                                return (
                                    <React.Fragment key={row.id}>
                                        <TableRow {...row.getRowProps()}>
                                            {row.cells.map((cell, idx) => {
                                                const td = cell.getCellProps()
                                                if (td && td.style) {
                                                    td.style = {
                                                        //allow columns to wrap when text too big
                                                        ...td.style,
                                                        whiteSpace: 'unset',
                                                        wordWrap: 'break-word',
                                                    }
                                                }
                                                return (
                                                    <TableCell
                                                        key={idx}
                                                        {...td}
                                                        // className="td"
                                                    >
                                                        {cell.render('Cell')}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                    </React.Fragment>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>
            </TableContainer>
        </>
    )
}

export default ReportTable
